// components/Header.js
import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useFontScale } from '../context/FontScaleContext';
import { LogOut, Home, ChevronDown, User, Users, Shield, Clock, Sparkles, Globe, KeyRound, Loader2, X, Search, Palette, Sun, Moon } from 'lucide-react';
import { ROUTE_META } from '../routesMeta';
import { ALL_MENUS } from './Sidebar';
import { TAB_REGISTRY } from '../searchRegistry';
import { readHistory, pushHistory, clearHistory } from '../utils/searchHistory';
import { GROUP_ORDER, GROUP_KEY, groupOfPermission } from '../config/navGroups';

// Map path → key judul (teks asli diambil dari translations via t()).
const PAGE_KEYS = {
  '/dashboard':             'overview',
  '/dashboard/aws-billing': 'awsBilling',
  '/dashboard/aws-cost':    'awsCost',
  '/dashboard/solman':      'solman',
  '/dashboard/sap-usage':   'sapUsage',
  '/dashboard/sap-user-maintenance': 'sapUserMgmt',
  '/dashboard/aws-inventory': 'awsInventory',
  '/dashboard/aws-cloudtrail': 'awsCloudTrail',
};

const ROLE_STYLES = {
  admin: { badge: 'bg-blue-100 text-blue-700',       icon: Shield, labelKey: 'role.admin'       },
  SAP:   { badge: 'bg-emerald-100 text-emerald-700', icon: User,   labelKey: 'role.sapOperator' },
  AWS:   { badge: 'bg-amber-100 text-amber-700',     icon: User,   labelKey: 'role.awsOperator' },
};

// ── Dashboard Access (collapsible) ───────────────────────────────────────────
// Daftar permission dulu tampil sebagai satu hamparan ~30 chip yang mendorong
// menu Sign Out ke bawah lipatan. Sekarang: default TERTUTUP, hanya menyisakan
// satu baris ringkas berisi jumlah izin; saat dibuka, chip dikelompokkan memakai
// peta yang sama dengan sidebar (config/navGroups.js) sehingga urutan dan nama
// kelompoknya tidak pernah menyimpang dari menu.
function DashboardAccess({ permissions }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);   // sengaja collapse by default

  const groups = React.useMemo(() => {
    const bucket = {};
    (permissions || []).forEach(p => {
      const g = groupOfPermission(p);
      (bucket[g] = bucket[g] || []).push(p);
    });
    // Ikuti GROUP_ORDER; kelompok kosong dilewati sendiri. Isi tiap kelompok
    // diurutkan agar posisi chip stabil antar-render dan antar-user.
    return GROUP_ORDER
      .filter(g => bucket[g]?.length)
      .map(g => ({ group: g, items: bucket[g].slice().sort() }));
  }, [permissions]);

  const total = (permissions || []).length;
  if (!total) return null;

  return (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors"
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <p className="text-xs text-slate-400 font-medium truncate">{t('header.dashboardAccess')}</p>
          <span className="px-1.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold tabular-nums flex-shrink-0">
            {total}
          </span>
        </span>
        <ChevronDown
          className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          style={{ width: 13, height: 13 }}
        />
      </button>

      {open && (
        <div className="px-4 pb-2.5 space-y-2 max-h-56 overflow-y-auto -mr-1 pr-1">
          {groups.map(({ group, items }) => (
            <div key={group}>
              <p className="text-[10px] font-semibold tracking-wide text-slate-400 mb-1">
                {t(GROUP_KEY[group]) || group}
              </p>
              <div className="flex flex-wrap gap-1">
                {items.map(p => (
                  <span key={p} className="px-2 py-0.5 bg-brand-50 text-brand-600 rounded text-xs font-medium">{p}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Quick Search (Ctrl+K) ─────────────────────────────────────────────────────
// Cari halaman + tab di dalam halaman. Hasil gabungan: halaman dulu, lalu tab.
// Tab yang user tidak punya akses tetap muncul tapi disabled (cursor-not-allowed).
function QuickSearch() {
  const { hasAccess, user } = useAuth();
  const { t }               = useLang();
  const navigate            = useNavigate();
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const [cursor, setCursor] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false); // narrow screens: box opened via icon button
  const [recent, setRecent] = useState(readHistory);
  const inputRef            = useRef(null);

  const isAdmin = user?.role === 'admin';
  const q       = query.trim().toLowerCase();
  // Every word must appear, any order: "notif history", "basis client copy".
  const tokens  = q.split(/\s+/).filter(Boolean);
  const matches = (text) => { const h = String(text || '').toLowerCase(); return tokens.every(tk => h.includes(tk)); };

  // Build lookup: pageKey → path (dari ROUTE_META)
  const pathMap = React.useMemo(() => {
    const m = {};
    ROUTE_META.forEach(r => { m[r.key] = r.path; });
    return m;
  }, []);

  // Helper: cek akses untuk permission tertentu
  const canAccess = (perm) => {
    if (!perm) return true;
    if (perm === '__admin__') return isAdmin;
    return hasAccess(perm);
  };

  // ── Hasil search: gabungan halaman + tab ──
  const results = React.useMemo(() => {
    if (!q) return [];

    // 1. Halaman (hanya yang accessible)
    const pages = ROUTE_META
      .filter(r => {
        if (!canAccess(r.permission)) return false;
        // Cocokkan juga dengan teks menu (label terjemahan, subjudul) dan key halaman.
        const mm = ALL_MENUS.find(m => m.key === r.key);
        const extra = [r.key.replace(/_/g, ' '), mm && t(mm.labelKey || ''), mm && t(mm.subKey || ''), mm && mm.fallback, mm && mm.fallbackSub]
          .filter(Boolean).join(' ').toLowerCase();
        return matches(r.label + ' ' + extra);
      })
      .map(r => ({
        id:       `page:${r.key}`,
        label:    r.label,
        type:     'page',
        path:     r.path,
        disabled: false,
      }));

    // 2. Tab (tampilkan semua yang match, tapi disabled jika tidak punya akses)
    //    Jangan tampilkan tab jika halaman induknya sudah tidak accessible
    const tabs = TAB_REGISTRY
      .filter(tr => {
        // Halaman induk harus accessible
        const pageMeta = ROUTE_META.find(r => r.key === tr.pageKey);
        if (!pageMeta || !canAccess(pageMeta.permission)) return false;
        return matches(tr.label + ' ' + tr.tabId + ' ' + (tr.keywords || ''));
      })
      .map(tr => ({
        id:       `tab:${tr.pageKey}:${tr.tabId}${tr.query ? ':' + tr.query : ''}`,
        label:    tr.label,
        type:     'tab',
        path:     `${pathMap[tr.pageKey]}?tab=${tr.tabId}${tr.query ? '&' + tr.query : ''}`,
        disabled: !canAccess(tr.permission),
      }));

    return [...pages, ...tabs].slice(0, 12); // cap 12 hasil
  }, [q, isAdmin]); // eslint-disable-line

  // Riwayat, DIVALIDASI ULANG terhadap izin saat ini. Entri lama bisa menunjuk
  // halaman yang izinnya sudah dicabut atau route yang sudah dihapus; menawarkan
  // tautan yang berujung "akses ditolak" lebih buruk daripada tidak menawarkan
  // apa pun. Yang tidak lolos DIBUANG diam-diam — ini daftar kenyamanan, bukan
  // laporan.
  const recentValid = React.useMemo(() => recent.filter(it => {
    if (it.type === 'tab') {
      const [, pageKey, tabId] = String(it.id).split(':');
      const pageMeta = ROUTE_META.find(r => r.key === pageKey);
      if (!pageMeta || !canAccess(pageMeta.permission)) return false;
      const tr = TAB_REGISTRY.find(x => x.pageKey === pageKey && String(x.tabId) === tabId);
      return !!tr && canAccess(tr.permission);
    }
    const key = String(it.id).replace(/^page:/, '');
    const meta = ROUTE_META.find(r => r.key === key);
    return !!meta && canAccess(meta.permission);
  }), [recent, isAdmin]); // eslint-disable-line

  // Daftar yang SEDANG dinavigasi papan ketik. Saat kotak kosong yang tampil
  // adalah riwayat, jadi panah atas/bawah dan Enter harus menunjuk ke sana —
  // kalau tetap menunjuk `results` (yang kosong), Enter tidak melakukan apa pun
  // padahal ada baris tersorot di layar.
  const shown = q ? results : recentValid;

  // Fokus otomatis saat MASUK dashboard.
  // Sekali saja, pada mount. Header ini hidup sepanjang sesi dashboard, jadi
  // mount = "baru masuk", bukan setiap pindah halaman — merebut fokus pada tiap
  // navigasi akan memotong ketikan pengguna di halaman tujuan.
  //
  // Dilewati pada perangkat sentuh dan layar sempit: kotak ini `hidden md:block`
  // (tidak ada di mobile), dan memaksa fokus ke elemen tersembunyi di sebagian
  // browser tetap memunculkan papan ketik virtual di atas halaman yang baru
  // dibuka.
  //
  // KOMPROMI yang disengaja: selama fokus di sini, Space dan PageDown mengetik
  // ke kotak cari alih-alih menggulir halaman. Karena itu dropdown TIDAK ikut
  // dibuka — kotaknya siap diketik, tetapi layar tidak tertutup panel, dan satu
  // Esc atau satu klik di mana pun mengembalikan keadaan normal.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const coarse = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    if (coarse || window.innerWidth < 768) return;
    // rAF: pada mount pertama, elemen lain (mis. field ber-autoFocus di halaman
    // tujuan) bisa mengambil fokus setelah kita. Menunggu satu frame membuat
    // urutannya deterministik.
    const id = requestAnimationFrame(() => {
      const el = document.activeElement;
      // Jangan rebut fokus dari elemen yang SUDAH aktif dan bisa diketik.
      const busy = el && el !== document.body
        && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (!busy) inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Ctrl+K global shortcut
  React.useEffect(() => {
    const handler = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const goTo = (item) => {
    if (item.disabled) return;
    setRecent(pushHistory(item));
    navigate(item.path);
    setQuery('');
    setOpen(false);
    setMobileOpen(false);
    setCursor(0);
  };

  // Keyboard: skip disabled items
  const nextEnabled = (from, dir) => {
    let i = from;
    while (i >= 0 && i < shown.length) {
      if (!shown[i].disabled) return i;
      i += dir;
    }
    return from; // fallback: stay
  };

  const handleKeyDown = e => {
    if (!open || shown.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => nextEnabled(Math.min(c + 1, shown.length - 1), 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => nextEnabled(Math.max(c - 1, 0), -1));
    }
    if (e.key === 'Enter') {
      const item = shown[cursor];
      if (item && !item.disabled) goTo(item);
    }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); setMobileOpen(false); inputRef.current?.blur(); }
  };

  const openMobile = () => { setMobileOpen(true); setOpen(true); setTimeout(() => inputRef.current?.focus(), 30); };

  return (
    <>
    {/* Mobile trigger: the box is hidden on narrow screens, so show an icon. */}
    {!mobileOpen && (
      <button type="button" onClick={openMobile} aria-label="Search pages"
        className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100">
        <Search style={{ width: 18, height: 18 }} />
      </button>
    )}
    <div className={`${mobileOpen ? 'fixed inset-x-0 top-0 z-[9990] bg-white p-3 shadow-md block' : 'relative hidden'} md:relative md:block md:inset-auto md:p-0 md:shadow-none md:bg-transparent md:z-auto`}>
      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:border-brand-300 focus-within:border-brand-400 focus-within:bg-white transition-all w-full md:w-56">
        <Search style={{ width: 14, height: 14 }} className="text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setCursor(0); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={`${t('header.search') || 'Cari halaman'}…`}
          className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none w-full"
        />
        {query ? (
          <button onClick={() => { setQuery(''); setOpen(false); }} className="text-slate-400 hover:text-slate-600">
            <X style={{ width: 12, height: 12 }} />
          </button>
        ) : (
          <kbd className="text-[10px] font-semibold text-slate-300 bg-slate-100 border border-slate-200 rounded px-1 py-0.5 leading-none">^K</kbd>
        )}
      </div>

      {/* Dropdown.
          Terbuka juga saat kotak KOSONG, asalkan ada riwayat — itulah inti
          fitur ini: klik kotaknya, tujuan yang biasa dibuka langsung terlihat
          tanpa mengetik apa pun. */}
      {open && q && shown.length === 0 && (
        <div className="absolute left-3 right-3 md:left-0 md:right-auto mt-2 md:w-80 bg-white rounded-xl shadow-lg border border-slate-100 z-[9989] px-3 py-3 text-sm text-slate-400">
          No pages or tabs match "{query.trim()}"
        </div>
      )}
      {open && shown.length > 0 && (
        <>
          <div className="fixed inset-0 z-[9980]" onClick={() => { setOpen(false); setQuery(''); setMobileOpen(false); }} />
          <div className="absolute left-3 right-3 md:left-0 md:right-auto mt-2 md:w-80 bg-white rounded-xl shadow-lg border border-slate-100 z-[9989] overflow-hidden max-h-80 overflow-y-auto">
            {!q && (
              <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {t('header.search.recent') || 'Terakhir dibuka'}
                </span>
                {/* onMouseDown+preventDefault: tanpa ini tombol mengambil fokus
                    dari input, input kehilangan fokus, dropdown tertutup — dan
                    klik-nya tidak pernah sampai ke onClick. */}
                <button
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setRecent(clearHistory()); inputRef.current?.focus(); }}
                  className="text-[10px] font-semibold text-slate-400 hover:text-slate-600">
                  {t('header.search.clearRecent') || 'Hapus'}
                </button>
              </div>
            )}
            {shown.map((item, i) => {
              const cls = `w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors
                  ${item.disabled
                    ? 'text-slate-300 cursor-not-allowed bg-slate-50/50'
                    : cursor === i
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`;
              const inner = (
                <>
                  {q
                    ? <Search style={{ width: 13, height: 13 }} className="flex-shrink-0 opacity-40" />
                    : <Clock style={{ width: 13, height: 13 }} className="flex-shrink-0 opacity-40" />}
                  <span className="truncate font-medium">{item.label}</span>
                  {item.type === 'tab' && (
                    <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${
                      item.disabled ? 'bg-slate-100 text-slate-300' : 'bg-brand-100 text-brand-500'
                    }`}>TAB</span>
                  )}
                  {item.disabled && (
                    <span className="ml-auto text-[10px] text-slate-300 flex-shrink-0">🔒</span>
                  )}
                </>
              );

              // Item yang TIDAK boleh diakses tetap <div>, bukan <a>: memberinya
              // href membuatnya bisa dibuka lewat klik-kanan / Ctrl+klik dan
              // melewati penjagaan yang justru jadi alasan ia ditandai disabled.
              if (item.disabled) {
                return (
                  <div key={item.id} aria-disabled="true" className={cls}>{inner}</div>
                );
              }

              // <a href> asli, bukan <button>: itulah yang membuat menu konteks
              // browser memunculkan "Open link in new tab", sekaligus mengaktifkan
              // Ctrl/Cmd+klik dan klik-tengah secara gratis. Navigasi normal
              // tetap lewat React Router (preventDefault) supaya SPA tidak
              // reload penuh.
              return (
                <a
                  key={item.id}
                  href={item.path}
                  onClick={e => {
                    // Biarkan browser menangani bila user MEMANG minta tab/window
                    // baru. Tanpa penjagaan ini, preventDefault akan menelan
                    // Ctrl+klik dan hasilnya membuka di tab yang sama.
                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    goTo(item);
                  }}
                  onMouseEnter={() => setCursor(i)}
                  className={cls}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </>
      )}
    </div>
    </>
  );
}

// ── Appearance Switcher (tema warna + ukuran font) ────────────────────────────
// Dua preferensi digabung dalam SATU popover, bukan dua tombol header terpisah:
// header sudah padat (tema, bahasa, home, blok user), dan keduanya sama-sama
// preferensi tampilan. Nama fungsi dipertahankan `ThemeSwitcher` supaya tidak
// perlu menyentuh pemanggilnya di bawah.
function ThemeSwitcher() {
  const { t } = useLang();
  const { theme, setTheme, presets, mode, setMode } = useTheme();
  const { fontScale, setFontScale, scales } = useFontScale();
  const [open, setOpen] = useState(false);
  const entries = Object.entries(presets);
  const fontEntries = Object.entries(scales);
  const current = presets[theme] || entries[0][1];

  // Terapkan lokal dulu supaya responsif, baru simpan ke server. Bila request
  // gagal (offline / sesi habis) tampilan tetap berubah dan pilihan tersimpan
  // di localStorage — hanya sinkronisasi lintas perangkat yang tertunda.
  const choose = (key) => {
    setTheme(key);
    setOpen(false);
    // Kegagalan JARINGAN memang boleh diabaikan (tampilan sudah berubah,
    // localStorage sudah menyimpan). Tapi 400 berarti server MENOLAK preset —
    // gejalanya identik dengan sukses, dan itu menyembunyikan ketidakcocokan
    // daftar preset frontend vs backend selama berbulan-bulan. Jadi dicatat.
    api.put('/api/auth/theme', { theme: key }).catch(err => {
      const st = err?.response?.status;
      if (st) console.warn(`[theme] server menolak preset '${key}' (HTTP ${st}) — `
        + 'tema hanya tersimpan di perangkat ini. Cek THEME_PRESETS di backend/server.js.');
    });
  };

  // Alasan pencatatan 400 sama persis seperti tema: tampilan sudah berubah dan
  // localStorage sudah menyimpan, jadi penolakan server TIDAK bergejala sama
  // sekali dan bisa lolos berbulan-bulan.
  const chooseFont = (key) => {
    setFontScale(key);
    setOpen(false);
    api.put('/api/auth/font-scale', { scale: key }).catch(err => {
      const st = err?.response?.status;
      if (st) console.warn(`[fontScale] server menolak nilai '${key}' (HTTP ${st}) — `
        + 'ukuran hanya tersimpan di perangkat ini. Cek FONT_SCALES di backend/server.js.');
    });
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        title={t('theme.label')}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-sm font-semibold transition-all border
          bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50"
      >
        {mode === 'dark'
          ? <Moon style={{ width: 15, height: 15 }} />
          : <Palette style={{ width: 15, height: 15 }} />}
        <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: current.swatch }} />
        <ChevronDown style={{ width: 12, height: 12 }} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[9990]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-100 z-[9999] overflow-hidden">
            {/* Mode terang/gelap — label literal Inggris (tanpa key i18n baru) */}
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
              Mode
            </div>
            <div className="p-2 grid grid-cols-2 gap-1.5 border-b border-slate-100">
              {[['light', 'Light', Sun], ['dark', 'Dark', Moon]].map(([key, label, Icon]) => (
                <button
                  key={key}
                  onClick={() => setMode(key)}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm border transition-colors
                    ${mode === key
                      ? 'bg-brand-50 text-brand-600 border-brand-200 font-semibold'
                      : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  <Icon style={{ width: 14, height: 14 }} />
                  {label}
                </button>
              ))}
            </div>
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
              {t('theme.label')}
            </div>
            {entries.map(([key, p]) => (
              <button
                key={key}
                onClick={() => choose(key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors
                  ${theme === key ? 'bg-brand-50 text-brand-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <span className="w-4 h-4 rounded-full border border-black/10 flex-shrink-0" style={{ background: p.swatch }} />
                {t(p.labelKey)}
                {theme === key && <span className="ml-auto text-brand-500">\u2713</span>}
              </button>
            ))}

            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-y border-slate-100">
              {t('fontScale.label')}
            </div>
            {fontEntries.map(([key, f]) => (
              <button
                key={key}
                onClick={() => chooseFont(key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors
                  ${fontScale === key ? 'bg-brand-50 text-brand-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {/* Pratinjau "Aa" dikunci ke px absolut, bukan rem — kalau tidak,
                    ketiga barisnya akan tampak sama besar karena ikut menskala
                    bersama ukuran yang sedang aktif. */}
                <span
                  className="w-6 flex-shrink-0 text-center leading-none"
                  style={{ fontSize: `${f.px}px` }}
                >
                  Aa
                </span>
                {t(f.labelKey)}
                {fontScale === key && <span className="ml-auto text-brand-500">\u2713</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Language Switcher ──────────────────────────────────────────────────────────
function LanguageSwitcher() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  const options = [
    { code: 'id', label: t('lang.indonesian'), flag: 'ID' },
    { code: 'en', label: t('lang.english'),    flag: 'EN' },
    { code: 'ar', label: 'العربية',            flag: 'AR' },
  ];
  const current = options.find(o => o.code === lang) || options[0];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        title={t('lang.label')}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-sm font-semibold transition-all border
          bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50"
      >
        <Globe style={{ width: 15, height: 15 }} />
        <span>{current.flag}</span>
        <ChevronDown style={{ width: 12, height: 12 }} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[9990]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-slate-100 z-[9999] overflow-hidden">
            {options.map(o => (
              <button
                key={o.code}
                onClick={() => { setLang(o.code); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors
                  ${lang === o.code ? 'bg-brand-50 text-brand-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <span className="w-6 text-xs font-bold text-slate-400">{o.flag}</span>
                {o.label}
                {lang === o.code && <span className="ml-auto text-brand-500">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Hook: data sesi aktif (admin only) — dipakai pill header & dropdown ────────
// Polling ringan ke /api/sessions/active tiap 45 dtk. enabled=false → tidak fetch.
function useActiveSessions(enabled) {
  const [info, setInfo] = useState(null);
  React.useEffect(() => {
    if (!enabled) { setInfo(null); return; }
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get('/api/sessions/active'); if (alive) setInfo(data); }
      catch { if (alive) setInfo(null); }
    };
    load();
    const id = setInterval(load, 45000);
    return () => { alive = false; clearInterval(id); };
  }, [enabled]);
  return info;
}

// "x menit lalu" sederhana dari timestamp SQLite (UTC 'YYYY-MM-DD HH:MM:SS').
function relTime(ts) {
  if (!ts) return '';
  const then = new Date(ts.replace(' ', 'T') + 'Z').getTime();
  const sec  = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (sec < 60)   return `${sec} dtk lalu`;
  const min = Math.round(sec / 60);
  if (min < 60)   return `${min} mnt lalu`;
  return `${Math.round(min / 60)} jam lalu`;
}

// Ringkas user-agent → label device (Chrome/Windows, Safari/iPhone, dll).
function deviceLabel(ua) {
  if (!ua) return 'Unknown';
  const os =
    /Windows/i.test(ua) ? 'Windows' :
    /iPhone|iPad/i.test(ua) ? 'iOS' :
    /Android/i.test(ua) ? 'Android' :
    /Mac OS X|Macintosh/i.test(ua) ? 'macOS' :
    /Linux/i.test(ua) ? 'Linux' : '';
  const br =
    /Edg\//i.test(ua) ? 'Edge' :
    /Chrome\//i.test(ua) ? 'Chrome' :
    /Firefox\//i.test(ua) ? 'Firefox' :
    /Safari\//i.test(ua) ? 'Safari' : 'Browser';
  return os ? `${br} · ${os}` : br;
}

// ── Modal Ganti Password (self-service, semua user) ───────────────────────────
function ChangePasswordModal({ onClose }) {
  const { t } = useLang();
  const [cur, setCur]       = useState('');
  const [next, setNext]     = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState(null);
  const [ok, setOk]         = useState(false);

  const submit = async () => {
    setErr(null);
    if (!cur || !next) { setErr(t('pwd.errRequired')); return; }
    if (next.length < 6) { setErr(t('pwd.errMinLength')); return; }
    if (next !== confirm) { setErr(t('pwd.errMismatch')); return; }
    if (next === cur) { setErr(t('pwd.errSameAsOld')); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword: cur, newPassword: next });
      setOk(true);
      setTimeout(onClose, 1400);
    } catch (e) {
      setErr(e.response?.data?.error || t('pwd.errGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
              <KeyRound className="text-brand-600" style={{ width: 16, height: 16 }} />
            </div>
            <h3 className="font-display font-bold text-slate-800 text-base">{t('pwd.title')}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {ok ? (
            <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 font-medium">
              {t('pwd.success')}
            </div>
          ) : (
            <>
              {err && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
                  {err}
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">{t('pwd.current')}</label>
                <input
                  type="password" autoComplete="current-password"
                  value={cur} onChange={e => setCur(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:border-brand-400 focus:bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">{t('pwd.new')}</label>
                <input
                  type="password" autoComplete="new-password"
                  value={next} onChange={e => setNext(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:border-brand-400 focus:bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">{t('pwd.confirm')}</label>
                <input
                  type="password" autoComplete="new-password"
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:border-brand-400 focus:bg-white"
                />
              </div>
            </>
          )}
        </div>

        {!ok && (
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50">
              {t('pwd.cancel')}
            </button>
            <button
              onClick={submit} disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white text-sm font-semibold rounded-xl transition-all"
            >
              {loading && <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />}
              {t('pwd.submit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Header({ onToggleAI, aiOpen, aiAvailable = true }) {
  const { user, logout } = useAuth();
  const { t, locale }    = useLang();
  const navigate         = useNavigate();
  const location         = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const isAdmin     = user?.role === 'admin';
  const sessionInfo = useActiveSessions(isAdmin);

  // Waktu mengikuti locale bahasa aktif; dihitung ulang saat bahasa berubah.
  const time = new Date().toLocaleString(locale, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const pageKey   = PAGE_KEYS[location.pathname];
  // Halaman yang belum punya teks page.* → judul & subjudul dari menu sidebar / ROUTE_META.
  const navMenu   = pageKey ? null : ALL_MENUS.find(m => m.path === location.pathname);
  const navRoute  = pageKey ? null : ROUTE_META.find(r => r.path === location.pathname);
  const tx        = (k, fb) => (k && t(k) !== k ? t(k) : fb);
  const title     = pageKey ? t(`page.${pageKey}.title`)
                  : navMenu ? tx(navMenu.labelKey, navMenu.fallback || (navRoute && navRoute.label) || t('page.default.title'))
                  : navRoute ? navRoute.label : t('page.default.title');
  const subtitle  = pageKey ? t(`page.${pageKey}.subtitle`) : navMenu ? tx(navMenu.subKey, navMenu.fallbackSub || '') : '';
  const roleStyle = ROLE_STYLES[user?.role] || { badge: 'bg-slate-100 text-slate-700', icon: User, labelKey: null };
  const RoleIcon  = roleStyle.icon;

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  return (
    <>
    <header className="h-16 bg-white border-b border-slate-100 shadow-sm flex items-center justify-between px-6 flex-shrink-0 z-30 relative">
      {/* Left: Page Title */}
      <div className="content-animate">
        <h1 className="font-display font-bold text-slate-800 text-lg leading-tight">{title}</h1>
        <p className="text-xs text-slate-400 font-medium">{subtitle}</p>
      </div>

      {/* Center: Quick Search */}
        <QuickSearch />

      {/* Right: Controls */}
      <div className="flex items-center gap-2">
        {/* Active Sessions pill (admin only) */}
        {isAdmin && sessionInfo && (
          <div
            title={`${sessionInfo.users} user · ${sessionInfo.devices} device — online ≤ ${sessionInfo.windowMinutes} menit`}
            className="hidden md:flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg border border-emerald-100"
          >
            <Users style={{ width: 14, height: 14 }} />
            <span>{sessionInfo.users}</span>
            <span className="opacity-60 font-medium">user</span>
            <span className="opacity-40">·</span>
            <span>{sessionInfo.devices}</span>
            <span className="opacity-60 font-medium">device</span>
          </div>
        )}

        {/* Timestamp */}
        <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-400 font-medium bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
          <Clock style={{ width: 14, height: 14 }} />
          <span>{time}</span>
        </div>

        {/* Language Switcher */}
        <ThemeSwitcher />
        <LanguageSwitcher />

        {/* AI Toggle Button — hanya tampil bila halaman terdaftar di AI Data Sources */}
        {aiAvailable && (
        <button
          onClick={onToggleAI}
          title={t('header.aiAssistant')}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all border
            ${aiOpen
              ? 'bg-brand-600 text-white border-brand-600 shadow-md'
              : 'bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50'
            }`}
        >
          <Sparkles style={{ width: 15, height: 15 }} />
          <span className="hidden sm:inline">{t('header.ai')}</span>
        </button>
        )}

        {/* Home — kembali ke Overview */}
        <button
          onClick={() => navigate('/dashboard')}
          title={t('header.home') !== 'header.home' ? t('header.home') : t('sidebar.overview')}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-brand-50 text-slate-400 hover:text-brand-600 transition-all border border-slate-100"
        >
          <Home style={{ width: 16, height: 16 }} />
        </button>

        {/* User Dropdown */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(p => !p)}
            className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-xl hover:bg-slate-50 transition-all border border-slate-100"
          >
            <div className="w-7 h-7 rounded-lg bg-brand-600 flex items-center justify-center text-white font-bold text-sm">
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-sm font-semibold text-slate-700 leading-tight">{user?.username}</div>
              <div className={`text-xs font-medium ${roleStyle.badge.split(' ')[1]}`}>{roleStyle.labelKey ? t(roleStyle.labelKey) : (user?.role || 'User')}</div>
            </div>
            <ChevronDown
              className={`text-slate-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
              style={{ width: 14, height: 14 }}
            />
          </button>

          {dropdownOpen && (
            <>
              <div className="fixed inset-0 z-[9990]" onClick={() => setDropdownOpen(false)} />
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-lg border border-slate-100 z-[9999] overflow-y-auto overflow-x-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex-shrink-0">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-brand-600 flex items-center justify-center text-white font-bold">
                      {user?.username?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-slate-800">{user?.username}</div>
                      <span className={`role-badge ${roleStyle.badge}`}>
                        <RoleIcon className="inline w-3 h-3 mr-1" style={{ width: 12, height: 12 }} />
                        {user?.role}
                      </span>
                    </div>
                  </div>
                </div>
                <DashboardAccess permissions={user?.permissions} />
                {isAdmin && sessionInfo && (
                  <div className="px-4 py-2.5 border-b border-slate-100">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Users style={{ width: 13, height: 13 }} className="text-emerald-600" />
                      <p className="text-xs text-slate-400 font-medium">
                        Sesi Aktif — {sessionInfo.users} user · {sessionInfo.devices} device
                        <span className="text-slate-300"> (≤{sessionInfo.windowMinutes}m)</span>
                      </p>
                    </div>
                    <div className="max-h-40 overflow-y-auto -mr-1 pr-1 space-y-1">
                      {(sessionInfo.sessions || []).length === 0 ? (
                        <p className="text-xs text-slate-400 italic">Tidak ada sesi aktif.</p>
                      ) : (
                        sessionInfo.sessions.map((s, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-slate-50">
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-slate-700 truncate">{s.username}</div>
                              <div className="text-[11px] text-slate-400 truncate">{deviceLabel(s.user_agent)}</div>
                            </div>
                            <span className="text-[11px] text-emerald-600 font-medium flex-shrink-0">{relTime(s.last_seen)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => { setDropdownOpen(false); setPwdModalOpen(true); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors font-medium border-b border-slate-100 flex-shrink-0"
                >
                  <KeyRound style={{ width: 15, height: 15 }} />
                  {t('pwd.menu')}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium flex-shrink-0"
                >
                  <LogOut style={{ width: 15, height: 15 }} />
                  {t('header.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
    {pwdModalOpen && <ChangePasswordModal onClose={() => setPwdModalOpen(false)} />}
    </>
  );
}
